<template>
  <div class="card patient-summary mb-3">
    <div class="card-header py-2 py-md-3">
      <h3 class="card-title align-items-start flex-column">
        <span class="card-label fw-bolder text-gray-800 fs-6 fs-md-5">Paciente</span>
      </h3>
      <div class="card-toolbar text-end">
        <button type="button" class="btn btn-sm btn-icon me-1" title="Editar paciente" @click="$emit('onEditPatient')">
          <i class="bi bi-pen fs-5"></i>
        </button>
        <button type="button" class="btn btn-sm btn-icon" title="Recolher dados do paciente" @click="toggleExpandPatientData">
          <i class="bi fs-5" :class="side === 'right' ? 'bi-arrow-right-circle' : 'bi-arrow-left-circle'"></i>
        </button>
      </div>
    </div>
    <div class="card-body py-3 py-md-4">
      <div v-if="$slots.image" class="patient-summary__video"><slot name="image" /></div>
      <div class="patient-summary__identity">
        <img v-if="!$slots.image" class="patient-summary__avatar"
             :src="patient?.patient_photo ? url(patient.patient_photo) : require('@/assets/images/no-photo.png')"
             alt="Foto do paciente">
        <div class="patient-summary__name">
          <strong>{{ patient?.patient_name }}</strong>
          <span v-if="patient?.patient_birthdate">{{ patient.patient_full_age }}</span>
          <span v-if="patient?.patient_birthdate">{{ date(patient.patient_birthdate).format('DD/MM/YYYY') }}</span>
        </div>
      </div>
      <div v-if="showConsultationTimer" class="patient-summary__timer">
        <span><i class="bi bi-alarm" aria-hidden="true"></i> Duração</span>
        <strong>{{ displayedTimer }}</strong>
      </div>
      <dl class="patient-summary__details">
        <template v-if="schedule?.payment_method?.paymeth_name">
          <dt>Pagamento</dt>
          <dd>{{ schedule.payment_method.paymeth_name }}<span v-if="schedule.payment_method.is_insurance && schedule?.insurance?.insurance_name"> · {{ schedule.insurance.insurance_name }}</span></dd>
        </template>
        <template v-if="schedule?.sched_time">
          <dt>Agendamento</dt><dd>{{ schedule.sched_time }}</dd>
        </template>
        <dt>CPF</dt><dd>{{ patient?.document_number || patient?.patient_cpf || '—' }}</dd>
        <template v-if="patientGenderLabel"><dt>Sexo</dt><dd>{{ patientGenderLabel }}</dd></template>
        <dt>Telefone</dt><dd>{{ patient?.patient_phone || '—' }}</dd>
        <dt>E-mail</dt><dd>{{ patient?.user_email || '—' }}</dd>
      </dl>

      <div class="separator my-2"></div>
      <div class="d-flex flex-stack fs-7 py-1">
        <div class="fw-bolder d-flex align-items-center gap-2">
          <i class="bi bi-tags-fill text-primary"></i> Tags
        </div>
        <button type="button" class="btn btn-sm btn-icon btn-light-primary" title="Gerenciar tags"
                :disabled="!professionalId" @click="openTagModal">
          <i class="bi bi-plus-lg"></i>
        </button>
      </div>
      <div class="separator my-2"></div>
      <div v-if="patientTags.length === 0" class="text-muted fs-8 py-1">
        Nenhuma tag registrada.
      </div>
      <div v-else class="d-flex flex-wrap gap-2 py-2">
        <span v-for="item in patientTags" :key="item.id_patient_tag"
              class="badge badge-light-primary d-inline-flex align-items-center gap-1 fs-7">
          {{ item.tag?.tag_name }}
          <button type="button" class="btn btn-icon p-0 ms-1" title="Remover"
                  @click="removePatientTag(item)">
            <i class="bi bi-x fs-8"></i>
          </button>
        </span>
      </div>

      <div class="separator my-2"></div>
      <div class="d-flex flex-stack fs-7 py-1">
        <div class="fw-bolder d-flex align-items-center gap-2">
          <i class="bi bi-exclamation-triangle-fill text-danger"></i> Alergias
        </div>
        <button type="button" class="btn btn-sm btn-icon btn-light-primary" title="Gerenciar alergias" @click="showAllergyModal = true">
          <i class="bi bi-plus-lg"></i>
        </button>
      </div>
      <div class="separator my-2"></div>
      <div v-if="patientAllergies.length === 0" class="text-muted fs-8 py-1">
        Nenhuma alergia registrada.
      </div>
      <div v-else>
        <div v-for="allergy in patientAllergies" :key="allergy.id_patient_allergy"
             class="allergy-item d-flex align-items-center justify-content-between py-2">
          <div class="d-flex align-items-center gap-2 min-w-0">
            <span class="badge badge-circle w-10px h-10px" :class="severityColor(allergy.patient_allergy_severity)"></span>
            <div class="min-w-0">
              <div class="fw-bold fs-7 text-truncate">{{ allergy.allergy?.allergy_name }}</div>
              <div class="text-muted fs-8" v-if="allergy.allergy?.category">{{ allergy.allergy.category.allergy_category_name }}</div>
              <div class="text-muted fs-8 fst-italic" v-if="allergy.patient_allergy_description">{{ allergy.patient_allergy_description }}</div>
            </div>
          </div>
          <button type="button" class="btn btn-sm btn-icon btn-light-danger flex-shrink-0" title="Remover"
                  @click="removeAllergy(allergy)">
            <i class="bi bi-x-lg fs-8"></i>
          </button>
        </div>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <div v-if="showAllergyModal" class="modal fade show d-block" tabindex="-1" @click.self="showAllergyModal = false">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Adicionar Alergia</h5>
            <button type="button" class="btn-close" @click="closeAllergyModal"></button>
          </div>
          <div class="modal-body">
            <div class="mb-4">
              <label class="form-label">Buscar alergia</label>
              <input type="text" class="form-control form-control-sm" v-model="allergySearch"
                     placeholder="Digite o nome da alergia..." @input="searchAllergies">
            </div>
            <div v-if="allergyCatalog.length" class="allergy-catalog-list" style="max-height: 200px; overflow-y: auto;">
              <div v-for="item in allergyCatalog" :key="item.id_allergy"
                   class="d-flex align-items-center justify-content-between py-2 px-2 rounded allergy-catalog-item"
                   style="cursor: pointer;" @click="selectAllergy(item)">
                <div>
                  <div class="fw-bold fs-7">{{ item.allergy_name }}</div>
                  <div class="text-muted fs-8" v-if="item.category">{{ item.category.allergy_category_name }}</div>
                </div>
                <i class="bi bi-plus-circle text-primary"></i>
              </div>
            </div>
            <div v-else-if="allergySearch.length >= 2" class="text-muted fs-7 text-center py-3">
              Nenhuma alergia encontrada.
            </div>
            <div v-if="selectedAllergy" class="mt-4 border rounded p-3">
              <div class="fw-bold mb-2">{{ selectedAllergy.allergy_name }}</div>
              <div class="mb-3">
                <label class="form-label fs-7">Gravidade</label>
                <select class="form-select form-select-sm" v-model="allergySeverity">
                  <option value="">Não informada</option>
                  <option value="leve">Leve</option>
                  <option value="moderada">Moderada</option>
                  <option value="grave">Grave</option>
                </select>
              </div>
              <div class="mb-3">
                <label class="form-label fs-7">Observação</label>
                <input type="text" class="form-control form-control-sm" v-model="allergyDescription"
                       placeholder="Observação (opcional)">
              </div>
              <button type="button" class="btn btn-sm btn-primary w-100" @click="storeAllergy" :disabled="storingAllergy">
                <span v-if="storingAllergy" class="spinner-border spinner-border-sm me-1"></span>
                Salvar Alergia
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showAllergyModal" class="modal-backdrop fade show"></div>

    <div v-if="showTagModal" class="modal fade show d-block" tabindex="-1" @click.self="closeTagModal">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Adicionar Tag</h5>
            <button type="button" class="btn-close" @click="closeTagModal"></button>
          </div>
          <div class="modal-body">
            <div class="mb-4">
              <label class="form-label">Buscar tag</label>
              <input type="text" class="form-control form-control-sm" v-model="tagSearch"
                     placeholder="Digite o nome da tag..." @input="searchTags">
            </div>
            <div v-if="visibleTagCatalog.length" class="tag-catalog-list" style="max-height: 200px; overflow-y: auto;">
              <div v-for="item in visibleTagCatalog" :key="item.id_tag"
                   class="d-flex align-items-center justify-content-between py-2 px-2 rounded tag-catalog-item"
                   style="cursor: pointer;" @click="assignCatalogTag(item)">
                <div class="fw-bold fs-7">{{ item.tag_name }}</div>
                <i class="bi bi-plus-circle text-primary"></i>
              </div>
            </div>
            <div v-else-if="tagSearch.trim().length >= 1 && !canCreateTag" class="text-muted fs-7 text-center py-3">
              Nenhuma tag encontrada.
            </div>
            <button v-if="canCreateTag" type="button" class="btn btn-sm btn-light-primary w-100 mt-3"
                    :disabled="storingTag" @click="createTagFromSearch">
              <span v-if="storingTag" class="spinner-border spinner-border-sm me-1"></span>
              Criar tag "{{ tagSearch.trim() }}"
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showTagModal" class="modal-backdrop fade show"></div>
  </Teleport>
</template>

<script>
import str from "../../../helpers/str";
import http from "../../../helpers/http";
import Alert from "../../../helpers/alert";

export default {
  name: "PatientCard",
  props: {
    side: { type: String, default: 'left' },
    patient: Object,
    schedule: Object,
    timerString: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      activeTimerString: 'Calculando...',
      counter: { seconds: 0, timer: null },
      patientAllergies: [],
      showAllergyModal: false,
      allergySearch: '',
      allergyCatalog: [],
      selectedAllergy: null,
      allergySeverity: '',
      allergyDescription: '',
      storingAllergy: false,
      searchTimeout: null,
      patientTags: [],
      showTagModal: false,
      tagSearch: '',
      tagCatalog: [],
      storingTag: false,
      tagSearchTimeout: null,
    }
  },
  async mounted() {
    this.startTimer();
    this.loadPatientAllergies();
    this.loadPatientTags();
  },
  beforeUnmount() {
    this.stopTimer();
    clearTimeout(this.tagSearchTimeout);
  },
  watch: {
    schedule() {
      this.stopTimer();
      this.startTimer();
    },
    'patient.id_patient'() {
      this.loadPatientAllergies();
      this.loadPatientTags();
    },
    professionalId() {
      this.loadPatientTags();
      this.loadPatientAllergies();
    },
  },
  computed: {
    showConsultationTimer() {
      return !!this.schedule?.sched_start_timezone;
    },
    patientGenderLabel() {
      if (this.patient?.patient_gender === 'Male') {
        return 'Masculino';
      }
      if (this.patient?.patient_gender === 'Female') {
        return 'Feminino';
      }
      return null;
    },
    displayedTimer() {
      return this.timerString || this.activeTimerString;
    },
    professionalId() {
      return this.$store.state.auth.user?.id_prof;
    },
    assignedTagIds() {
      return this.patientTags.map((item) => item.id_tag);
    },
    visibleTagCatalog() {
      return this.tagCatalog.filter((item) => !this.assignedTagIds.includes(item.id_tag));
    },
    canCreateTag() {
      const search = (this.tagSearch || '').trim();
      if (!search) {
        return false;
      }
      const normalized = search.toLowerCase();
      return !this.tagCatalog.some((item) => (item.tag_name || '').toLowerCase() === normalized);
    },
  },
  methods: {
    date: moment,
    url(path) {
      return str.url(path)
    },
    severityColor(severity) {
      const colors = {
        'leve': 'bg-warning',
        'moderada': 'bg-info',
        'grave': 'bg-danger',
      };
      return colors[severity] || 'bg-secondary';
    },
    loadPatientTags() {
      const patientId = this.patient?.id_patient;
      if (!patientId || !this.professionalId) {
        this.patientTags = [];
        return;
      }
      http.get(`/professionals/${this.professionalId}/patients/${patientId}/tags`).then((response) => {
        this.patientTags = response.data;
      });
    },
    openTagModal() {
      this.showTagModal = true;
      this.tagSearch = '';
      this.loadTagCatalog();
    },
    loadTagCatalog() {
      if (!this.professionalId) {
        this.tagCatalog = [];
        return;
      }
      const params = {};
      const search = (this.tagSearch || '').trim();
      if (search.length >= 1) {
        params.search = search;
      }
      http.get(`/professionals/${this.professionalId}/tags/catalog`, { params }).then((response) => {
        this.tagCatalog = response.data;
      });
    },
    searchTags() {
      clearTimeout(this.tagSearchTimeout);
      this.tagSearchTimeout = setTimeout(() => {
        this.loadTagCatalog();
      }, 300);
    },
    assignCatalogTag(tag) {
      this.storePatientTag({ id_tag: tag.id_tag });
    },
    createTagFromSearch() {
      const tagName = (this.tagSearch || '').trim();
      if (!tagName) {
        return;
      }
      this.storePatientTag({ tag_name: tagName });
    },
    storePatientTag(payload) {
      const patientId = this.patient?.id_patient;
      if (!patientId || !this.professionalId || this.storingTag) {
        return;
      }
      this.storingTag = true;
      http.post(`/professionals/${this.professionalId}/patients/${patientId}/tags`, payload).then(() => {
        this.loadPatientTags();
        this.tagSearch = '';
        this.loadTagCatalog();
        Alert.toastSuccess('Tag registrada com sucesso.');
      }).catch((error) => {
        Alert.toastError(error.response?.data?.message || 'Erro ao registrar tag.');
      }).finally(() => {
        this.storingTag = false;
      });
    },
    removePatientTag(item) {
      Swal.fire({
        title: 'Atenção',
        text: 'Deseja remover esta tag do paciente?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, remover!',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#780f5a',
      }).then((result) => {
        if (!result.isConfirmed) {
          return;
        }
        const patientId = this.patient?.id_patient;
        http.delete(`/professionals/${this.professionalId}/patients/${patientId}/tags/${item.id_patient_tag}`).then(() => {
          this.loadPatientTags();
          Alert.toastSuccess('Tag removida.');
        }).catch((error) => {
          Alert.toastError(error.response?.data?.message || 'Erro ao remover tag.');
        });
      });
    },
    closeTagModal() {
      this.showTagModal = false;
      this.tagSearch = '';
      this.tagCatalog = [];
    },
    loadPatientAllergies() {
      const patientId = this.patient?.id_patient;
      if (!patientId || !this.professionalId) {
        this.patientAllergies = [];
        return;
      }
      http.get(`/professionals/${this.professionalId}/patients/${patientId}/allergies`).then((response) => {
        this.patientAllergies = response.data;
      });
    },
    searchAllergies() {
      clearTimeout(this.searchTimeout);
      if (this.allergySearch.length < 2) {
        this.allergyCatalog = [];
        return;
      }
      this.searchTimeout = setTimeout(() => {
        http.get(`/professionals/${this.professionalId}/allergies/catalog`, {
          params: { search: this.allergySearch }
        }).then((response) => {
          this.allergyCatalog = response.data;
        });
      }, 300);
    },
    selectAllergy(allergy) {
      this.selectedAllergy = allergy;
      this.allergySeverity = '';
      this.allergyDescription = '';
    },
    storeAllergy() {
      if (!this.selectedAllergy) {
        return;
      }
      this.storingAllergy = true;
      const patientId = this.patient?.id_patient;
      http.post(`/professionals/${this.professionalId}/patients/${patientId}/allergies`, {
        id_allergy: this.selectedAllergy.id_allergy,
        patient_allergy_severity: this.allergySeverity || null,
        patient_allergy_description: this.allergyDescription || null,
      }).then(() => {
        this.loadPatientAllergies();
        this.resetAllergyForm();
        Alert.toastSuccess('Alergia registrada com sucesso.');
      }).catch((error) => {
        Alert.toastError(error.response?.data?.message || 'Erro ao registrar alergia.');
      }).finally(() => {
        this.storingAllergy = false;
      });
    },
    removeAllergy(allergy) {
      Swal.fire({
        title: 'Atenção',
        text: 'Deseja remover esta alergia do paciente?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, remover!',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#780f5a',
      }).then((result) => {
        if (!result.isConfirmed) {
          return;
        }
        const patientId = this.patient?.id_patient;
        http.delete(`/professionals/${this.professionalId}/patients/${patientId}/allergies/${allergy.id_patient_allergy}`).then(() => {
          this.loadPatientAllergies();
          Alert.toastSuccess('Alergia removida.');
        }).catch((error) => {
          Alert.toastError(error.response?.data?.message || 'Erro ao remover alergia.');
        });
      });
    },
    closeAllergyModal() {
      this.showAllergyModal = false;
      this.resetAllergyForm();
    },
    resetAllergyForm() {
      this.allergySearch = '';
      this.allergyCatalog = [];
      this.selectedAllergy = null;
      this.allergySeverity = '';
      this.allergyDescription = '';
    },
    _readableTimeFromSeconds: function(seconds) {
      const hours = 3600 > seconds ? 0 : parseInt(seconds / 3600, 10)
      return {
        hours: this._padNumber(hours),
        seconds: this._padNumber(seconds % 60),
        minutes: this._padNumber(parseInt(seconds / 60, 10) % 60),
      }
    },
    _padNumber: number =>  (number > 9 || number === 0) ? number : "0" + number,
    toggleExpandPatientData() {
      this.$emit('onToggleExpandPatientData', true);
    },
    startTimer: function () {
      if (this.timerString || !this.showConsultationTimer) {
        return;
      }

      const started = moment(this.schedule.sched_start_timezone)

      this.counter.seconds = parseInt(moment.duration(moment().diff(started)).asSeconds())
      this.counter.timer = setInterval(() => {
        const time = this._readableTimeFromSeconds(++this.counter.seconds)
        this.activeTimerString = `${time.hours}:${time.minutes}:${time.seconds}`
        this.$emit('onTimerUpdate', this.activeTimerString)
      }, 1000)
    },
    stopTimer: function () {
      clearInterval(this.counter.timer);
      this.counter = { seconds: 0, timer: null }
      this.activeTimerString = 'Calculando...'
    },
  }
}
</script>

<style scoped>
.patient-summary > .card-header { min-height: 0; padding: 8px 16px !important; }
.patient-summary > .card-header .card-title { margin: 0; }
.patient-summary > .card-header .card-label { font-size: 13px !important; }
.patient-summary > .card-body { padding: 12px 16px !important; }
.patient-summary > .card-header .btn-icon, .patient-summary > .card-body .btn-sm.btn-icon { width: 28px; height: 28px; }
.patient-summary__identity { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; }
.patient-summary__avatar { flex-shrink: 0; width: 44px; height: 44px; object-fit: cover; border-radius: 50%; }
.patient-summary__name { min-width: 0; }
.patient-summary__name strong { display: block; font-size: 13px; line-height: 1.4; color: #302b38; overflow-wrap: anywhere; }
.patient-summary__name span { display: block; font-size: 11px; line-height: 1.5; color: #706b79; }
.patient-summary__timer { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 8px 10px; margin-bottom: 12px; border-radius: 8px; background: #f7edf4; color: #780f5a; }
.patient-summary__timer span { display: flex; align-items: center; gap: 6px; font-size: 11px; }
.patient-summary__timer strong { font-size: 15px; font-variant-numeric: tabular-nums; }
.patient-summary__details { display: grid; grid-template-columns: auto minmax(0, 1fr); gap: 7px 12px; margin: 0 0 12px; font-size: 12px; line-height: 1.4; }
.patient-summary__details dt { font-weight: 400; color: #817b89; }
.patient-summary__details dd { margin: 0; color: #45404d; text-align: right; overflow-wrap: anywhere; }
.patient-summary__video { margin-bottom: 12px; }

/* Otimizações para telas menores */
@media (max-width: 1366px) {
  .card {
    margin-bottom: 1rem !important;
  }
  
  .card-header {
    padding: 0.5rem 1rem !important;
  }
  
  .card-body {
    padding: 0.75rem 1rem !important;
  }
  
  .symbol {
    width: 50px !important;
    height: 50px !important;
  }
  
  .symbol img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

@media (max-width: 768px) {
  .card-header {
    padding: 0.4rem 0.75rem !important;
  }
  
  .card-body {
    padding: 0.5rem 0.75rem !important;
  }
  
  .symbol {
    width: 45px !important;
    height: 45px !important;
  }
  
  .fs-7 {
    font-size: 0.75rem !important;
  }
  
  .mb-1 {
    margin-bottom: 0.25rem !important;
  }
  
  .mb-2 {
    margin-bottom: 0.5rem !important;
  }
}

/* Melhorias gerais para compactação */
.card-label {
  line-height: 1.2;
}

.text-center {
  word-break: break-word;
}

/* Timer section mais compacto */
.border-dashed {
  border-style: dashed !important;
}

/* Otimização do layout dos dados */
.fw-bolder {
  font-weight: 600 !important;
}

.text-gray-600 {
  color: #6c757d !important;
}

.allergy-item {
  border-bottom: 1px dashed #e4e6ef;
}

.allergy-item:last-child {
  border-bottom: none;
}

.allergy-catalog-item:hover,
.tag-catalog-item:hover {
  background-color: #f5f8fa;
}

.cursor-pointer {
  cursor: pointer;
}

.modal-backdrop {
  z-index: 1040;
}

.modal {
  z-index: 1050;
}
</style>